import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { Spin } from 'antd';

import SelectWithInfiniteScroller from 'components/atoms/SelectWithInfiniteScroller';
import ErrorBox from 'components/molecules/ErrorBox/ErrorBox';
import Loader from 'components/molecules/Loader';
import { tradePromoOptimizationUrls } from 'config/urls';
import { useCommonApiHandler } from 'hooks/general/useCommonApiHandler';
import type { AttributeOption, ResolvedField } from 'types/tpo/reportSetup';
import {
    computeNextExclusion,
    formatAttributeOptionLabel,
    normalizeAttributeExId,
    PAGE_LIMIT,
    toAttributeFieldValue,
} from 'utils/tpoReportSetupHelpers';

type AttributeSelectFieldProps = {
    field: ResolvedField;
    value: any;
    disabled: boolean;
    multi: boolean;
    onChange: (val: any) => void;
    buildFilters: (skipFieldKey: string) => Record<string, any>;
    selectAllConfig?: { enabled: boolean };
    selectedFilter: ResolvedField | null;
    onFieldOpen: (field: ResolvedField) => void;
    viewMode?: boolean;
    presetOptions?: AttributeOption[];
};

const AttributeSelectField: FC<AttributeSelectFieldProps> = ({
    field,
    value,
    disabled,
    multi,
    onChange,
    buildFilters,
    selectAllConfig,
    selectedFilter,
    onFieldOpen,
    viewMode = false,
    presetOptions,
}) => {
    const [offset, setOffset] = useState(0);
    const [searchKey, setSearchKey] = useState('');
    const [searchInput, setSearchInput] = useState('');
    const [list, setList] = useState<AttributeOption[]>(() => (viewMode && presetOptions?.length ? presetOptions : []));
    const [count, setCount] = useState(0);

    useEffect(() => {
        if (!viewMode) return;
        setList(presetOptions ?? []);
    }, [viewMode, presetOptions]);

    const isActive = !viewMode && selectedFilter?.key === field.key;

    const filters = useMemo(() => (isActive ? buildFilters(field.key) : null), [selectedFilter, field.key]);

    const variables = useMemo(
        () => ({
            attribute_type: field.key,
            dimension: field.dimension,
            attribute_config_id: field.attributeConfigId,
            limit: PAGE_LIMIT,
            offset,
            search_key: searchKey,
            filters,
        }),
        [field.key, field.dimension, field.attributeConfigId, offset, searchKey, filters],
    );

    // Reset paging when this field becomes active again (new selectedFilter identity) or
    // when the user commits a new search via Enter.
    useEffect(() => {
        setOffset(0);
    }, [selectedFilter, searchKey]);

    const { loading: fetchLoading, error: fetchError } = useCommonApiHandler({
        method: 'post',
        url: tradePromoOptimizationUrls.getReportsFilterAttributeListUrl,
        queryKey: ['tpo-reports-filter-attribute-list', field.key, variables],
        variables,
        queryConfig: {
            // Never fetch in view mode — the dropdown is disabled and `list` is
            // seeded from the saved report's filters instead.
            enabled: !viewMode && isActive && filters != null && !!field.attributeConfigId && !!field.dimension,
        },
        parserFunction: (res: any) => {
            const rows: AttributeOption[] = (res?.attributes ?? []).map((a: any) => ({
                label: formatAttributeOptionLabel(a.attributeDesc, a.attributeExId, a.attributeType),
                value: normalizeAttributeExId(a.attributeExId),
                attributeType: a.attributeType,
                dimension: a.dimension,
                attributeConfigId: a.attributeConfigId,
            }));
            if (offset > 0) {
                setList((prev) => [...prev, ...rows]);
            } else {
                setList(rows);
                setCount(res?.total_count ?? 0);
            }
            return { data: rows, count: res?.total_count ?? 0 };
        },
    });

    const onInfiniteScrollOnNextMemoised = useCallback(() => {
        if (fetchLoading) return;
        if (list.length >= count) return;
        setOffset((o) => o + PAGE_LIMIT);
    }, [fetchLoading, list.length, count]);

    // Attribute value model: { all, values }.
    // all=false -> `values` is the inclusion list (picked items).
    // all=true  -> "Select All" is active; `values` is the exclusion list (items removed from "all").
    const normalizedValue = toAttributeFieldValue(value);
    const isAllMode = multi && normalizedValue.all;
    const excludedSet = useMemo(() => new Set(normalizedValue.values.map(String)), [normalizedValue.values]);

    const mergedOptions = useMemo(() => {
        let merged: AttributeOption[];
        if (!presetOptions?.length) {
            merged = list;
        } else {
            const seenValues = new Set<string | number>(list.map((option) => option.value));
            const extraPresets = presetOptions.filter((option) => !seenValues.has(option.value));
            merged = extraPresets.length ? [...list, ...extraPresets] : list;
        }

        // plan version labels to uppercase
        if (field.key === 'plan_version') {
            return merged.map((option) => ({ ...option, label: String(option.label ?? '').toUpperCase() }));
        }
        return merged;
    }, [list, presetOptions, field.key]);

    // In "Select All" (exclude) mode every loaded option is selected except the excluded ones.
    const allOptionValues = mergedOptions.map((o) => o.value);
    const displaySelected = isAllMode
        ? allOptionValues.filter((v) => !excludedSet.has(String(v)))
        : normalizedValue.values;

    // Toggle "Select All" header: enter exclude-all mode or fall back to an empty inclusion.
    const handleSelectAllToggle = () => onChange(isAllMode ? { all: false, values: [] } : { all: true, values: [] });

    const handleChange = (val: any) => {
        if (viewMode) return;
        // Clear the search text after every selection.
        setSearchInput('');
        if (!multi) {
            onChange({ all: false, values: val === null || val === undefined ? [] : [val] });
            return;
        }
        const newArr: (string | number)[] = Array.isArray(val) ? val : [];
        if (isAllMode) {
            // Diff against the current display to maintain the exclusion list.
            onChange({ all: true, values: computeNextExclusion(normalizedValue.values, displaySelected, newArr) });
            return;
        }
        onChange({ all: false, values: newArr });
    };

    // page-0 refetch in flight with stale rows still rendered — drives the dimming overlay
    const isRefetching = fetchLoading && offset === 0 && list.length > 0;

    // first page-0 fetch with empty list — swaps stale preset rows for a clean loader (edit mode)
    const isFirstFetching = fetchLoading && offset === 0 && list.length === 0;

    return (
        <SelectWithInfiniteScroller
            hasMore={!viewMode && list.length < count}
            onInfiniteScrollOnNextMemoised={onInfiniteScrollOnNextMemoised}
            loading={!viewMode && fetchLoading}
            className="w-full"
            style={{ width: '100%' }}
            mode={multi ? 'multiple' : undefined}
            maxTagCount={multi ? 'responsive' : undefined}
            placeholder={isAllMode ? 'All selected' : field.placeholder ?? 'Select'}
            value={multi ? displaySelected : normalizedValue.values[0] ?? undefined}
            options={mergedOptions}
            showArrow
            showSearch={!viewMode}
            allowClear={!viewMode}
            autoClearSearchValue={false}
            searchValue={viewMode ? '' : searchInput}
            disabled={viewMode ? false : disabled}
            removeIcon={viewMode ? null : undefined}
            filterOption={false}
            defaultActiveFirstOption={false}
            onVisibilityChange={(open: boolean) => {
                if (viewMode) return;
                if (open) onFieldOpen(field);
            }}
            onSearch={viewMode ? undefined : (v: string) => setSearchInput(v)}
            onInputKeyDown={
                viewMode
                    ? undefined
                    : (e: React.KeyboardEvent<HTMLInputElement>) => {
                          if (e.key === 'Enter') {
                              const val = (e.target as HTMLInputElement)?.value ?? '';
                              setSearchKey(val);
                          }
                      }
            }
            notFoundContent={
                fetchLoading ? (
                    <div className="p-3">
                        <Loader />
                    </div>
                ) : fetchError ? (
                    <ErrorBox msg={typeof fetchError === 'string' ? fetchError : 'Failed to load'} />
                ) : viewMode ? (
                    <div className="text-fontGreyAlt font-xs py-4 text-center">Not Selected</div>
                ) : (
                    <ErrorBox msg="No Data" />
                )
            }
            dropdownRender={(menu) => (
                <div className="relative">
                    {!viewMode &&
                        multi &&
                        selectAllConfig?.enabled &&
                        !isRefetching &&
                        (isAllMode || mergedOptions.length > 0) && (
                            <label
                                className="font-xs flex items-center gap-2 cursor-pointer px-3 py-2 border-b border-backgroundGreyAlt6"
                                // Prevent the dropdown from closing on click.
                                onMouseDown={(e) => e.preventDefault()}
                            >
                                <input
                                    type="checkbox"
                                    className="checkbox-size accent-purpleFont"
                                    checked={isAllMode}
                                    onChange={handleSelectAllToggle}
                                />
                                <span>Select All</span>
                            </label>
                        )}
                    {isFirstFetching ? (
                        <div className="p-3 flex justify-center">
                            <Loader />
                        </div>
                    ) : (
                        <>
                            <div className={isRefetching ? 'opacity-40 pointer-events-none' : ''}>{menu}</div>
                            {isRefetching && (
                                <div className="absolute inset-0 flex items-center justify-center">
                                    <Spin />
                                </div>
                            )}
                        </>
                    )}
                </div>
            )}
            onChange={handleChange}
        />
    );
};

export default AttributeSelectField;
